const template = document.createElement('template');
template.innerHTML = `
<style>
    :host {
        display: inline-block;
        font-family: 'Courier New', Courier, monospace;
        user-select: none;
        width: 100px; /* Default */
        position: relative;
        font-size: 10px;
    }
    .trigger {
        background: rgba(0, 0, 0, 0.3);
        border: 1px solid #444;
        color: #fff;
        padding: 4px 6px;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        transition: all 0.2s;
        box-shadow: inset 0 1px 3px rgba(0,0,0,0.5);
    }
    .trigger:hover {
        border-color: #666;
        background: rgba(255, 255, 255, 0.05);
    }
    .trigger.active {
        border-color: var(--primary-color, #00ffcc);
        box-shadow: 0 0 5px rgba(0, 255, 204, 0.2);
    }
    .arrow {
        width: 0; 
        height: 0; 
        border-left: 3px solid transparent;
        border-right: 3px solid transparent;
        border-top: 4px solid #aaa;
        margin-left: 6px;
        transition: transform 0.2s;
    }
    .trigger.active .arrow {
        transform: rotate(180deg);
        border-top-color: var(--primary-color, #00ffcc);
    }
    .selected-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
    }
    
    .dropdown-list {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        max-height: 200px;
        overflow-y: auto;
        background: rgba(20, 20, 20, 0.95);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 1px solid #444;
        border-radius: 4px;
        margin-top: 4px;
        z-index: 1000;
        display: none;
        flex-direction: column;
        box-shadow: 0 4px 10px rgba(0,0,0,0.5);
    }
    .dropdown-list::-webkit-scrollbar {
        width: 6px;
    }
    .dropdown-list::-webkit-scrollbar-track {
        background: rgba(0,0,0,0.3);
    }
    .dropdown-list::-webkit-scrollbar-thumb {
        background: #444;
        border-radius: 3px;
    }
    .dropdown-list::-webkit-scrollbar-thumb:hover {
        background: var(--primary-color, #00ffcc);
    }
    
    .dropdown-list.open {
        display: flex;
        animation: fadeIn 0.1s ease-out;
    }
    
    .option {
        padding: 6px 8px;
        cursor: pointer;
        color: #aaa;
        border-bottom: 1px solid rgba(255,255,255,0.05);
        transition: all 0.1s;
    }
    .option:last-child {
        border-bottom: none;
    }
    .option:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        padding-left: 12px;
    }
    .option.selected {
        color: var(--primary-color, #00ffcc);
        background: rgba(0, 255, 204, 0.05);
        font-weight: bold;
    }
    
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-5px); }
        to { opacity: 1; transform: translateY(0); }
    }
</style>
<div class="trigger" id="trigger">
    <span class="selected-text" id="selected-text">Select...</span>
    <div class="arrow"></div>
</div>
<div class="dropdown-list" id="list"></div>
<slot style="display: none;"></slot>
`;

export class CustomDropdown extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.shadowRoot.appendChild(template.content.cloneNode(true));
        
        this.trigger = this.shadowRoot.getElementById('trigger');
        this.list = this.shadowRoot.getElementById('list');
        this.textEl = this.shadowRoot.getElementById('selected-text');
        this.slotEl = this.shadowRoot.querySelector('slot');
        
        this._value = '';
        this._options = [];
        this._isOpen = false;
        
        // Bindings
        this.toggle = this.toggle.bind(this);
        this.close = this.close.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        
        this.trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggle();
        });
        
        this.slotEl.addEventListener('slotchange', () => this.parseOptions());
    }

    connectedCallback() {
        // Initial parse if children exist
        this.parseOptions();
        document.addEventListener('click', this.handleOutsideClick);
    }
    
    disconnectedCallback() {
        document.removeEventListener('click', this.handleOutsideClick);
    }

    parseOptions() {
        this._options = [];
        const nodes = this.slotEl.assignedElements();
        // If no slot assigned, check direct children (fallback)
        const children = nodes.length ? nodes : Array.from(this.children);
        
        children.forEach(el => {
            if (el.tagName === 'OPTION') {
                this._options.push({
                    label: el.textContent,
                    value: el.getAttribute('value')
                });
            }
        });
        
        this.renderList();
        
        // Set initial selection if needed
        if (!this._value && this._options.length > 0) {
            // Check for 'selected' attribute on children
            const selectedChild = children.find(el => el.hasAttribute('selected'));
            if (selectedChild) {
                this.value = selectedChild.getAttribute('value');
            } else {
                this.value = this._options[0].value;
            }
        } else {
            // Refresh display
            this.updateDisplay();
        }
    }

    renderList() {
        this.list.innerHTML = '';
        this._options.forEach(opt => {
            const div = document.createElement('div');
            div.className = 'option';
            div.textContent = opt.label;
            if (opt.value === this._value) div.classList.add('selected');
            
            div.addEventListener('click', (e) => {
                e.stopPropagation();
                this.value = opt.value;
                this.close();
                this.dispatchEvent(new Event('change', { bubbles: true }));
                this.dispatchEvent(new Event('input', { bubbles: true }));
            });
            
            this.list.appendChild(div);
        });
    }

    get value() { return this._value; }
    set value(val) {
        if (this._value === val) return;
        this._value = val;
        this.updateDisplay();
        this.renderList(); // Re-render to update selected class
    }
    
    // Add selectedIndex/options getter for compatibility if needed? 
    // Usually 'value' is enough for this app.
    get options() { return this.children; }
    get selectedIndex() { return this._options.findIndex(o => o.value === this._value); }

    updateDisplay() {
        const selected = this._options.find(o => o.value === this._value);
        if (selected) {
            this.textEl.textContent = selected.label;
        }
    }

    toggle() {
        if (this._isOpen) this.close();
        else this.open();
    }

    open() {
        this._isOpen = true;
        this.list.classList.add('open');
        this.trigger.classList.add('active');
        this.setAttribute('open', '');
    }

    close() {
        this._isOpen = false;
        this.list.classList.remove('open');
        this.trigger.classList.remove('active');
        this.removeAttribute('open');
    }

    handleOutsideClick(e) {
        if (this._isOpen && !this.contains(e.target)) {
            this.close();
        }
    }
}

customElements.define('custom-dropdown', CustomDropdown);
